import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Screen } from '../components/AppShell';
import { StatusText, StatusPill, Icon, Skeleton, EmptyState, Sheet, Field } from '../components/ui';
import { api, type Customer, type Invoice, type Job } from '../lib/api';
import { useAction, useLoad } from '../lib/hooks';
import { formatDate } from '../../../shared/schedule';
import './screens.css';

export default function Invoices() {
  const [creating, setCreating] = useState(false);
  const { data, loading, error, reload } = useLoad<{ invoices: Invoice[] }>(() => api.get('/invoices'));

  const outstanding = useMemo(
    () =>
      (data?.invoices ?? [])
        .filter((i) => i.status === 'sent' || i.status === 'overdue')
        .reduce((sum, i) => sum + Number(i.total), 0),
    [data],
  );

  return (
    <Screen
      onRefresh={reload}
      title="Invoices"
      back="/more"
      action={
        <button className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>
          <Icon name="plus" size={17} />
          New
        </button>
      }
    >
      <div className="stack-4">
        {outstanding > 0 ? (
          <div className="notice notice-warn">
            <span className="small">
              <strong className="money">£{outstanding.toFixed(2)}</strong> outstanding across unpaid invoices.
            </span>
          </div>
        ) : null}

        {error ? <div className="notice notice-danger">{error}</div> : null}
        {loading && !data ? <Skeleton rows={5} /> : null}

        {data && data.invoices.length === 0 ? (
          <EmptyState
            title="No invoices yet"
            body="Completed workshop jobs can be turned into an invoice in one step."
            action={
              <button className="btn btn-primary" onClick={() => setCreating(true)}>
                Create an invoice
              </button>
            }
          />
        ) : null}

        {data && data.invoices.length > 0 && (
          <div className="panel divide">
            {data.invoices.map((invoice) => (
              <Link key={invoice.id} to={`/invoices/${invoice.id}`} className="listrow">
                <div className="listrow-lead" style={{ minWidth: '5.5rem' }}>
                  <span className="mono small">{invoice.number}</span>
                </div>
                <div className="grow">
                  <div className="listrow-title truncate">{invoice.customer_name}</div>
                  <div className="listrow-sub">
                    {formatDate(invoice.issue_date)}
                    {invoice.due_date ? (
                      <>
                        <span className="dot" />
                        due {formatDate(invoice.due_date)}
                      </>
                    ) : null}
                  </div>
                </div>
                <span className="money">£{Number(invoice.total).toFixed(2)}</span>
                {invoice.status === 'overdue' ? (
                  <StatusPill state="overdue" size="sm">
                    overdue
                  </StatusPill>
                ) : (
                  <StatusText state={invoice.status === 'paid' ? 'ok' : invoice.status === 'draft' ? 'unknown' : 'due'}>
                    {invoice.status}
                  </StatusText>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>

      <CreateInvoice
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => {
          setCreating(false);
          reload();
        }}
      />
    </Screen>
  );
}

function CreateInvoice({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { busy, error, run } = useAction();
  const [customerId, setCustomerId] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data: customers } = useLoad<{ customers: Customer[] }>(() => api.get('/customers'));
  const { data: jobs } = useLoad<{ jobs: Job[] }>(() => api.get('/jobs?status=complete'));

  const available = useMemo(
    () => (jobs?.jobs ?? []).filter((j) => !customerId || j.customer_id === customerId),
    [jobs, customerId],
  );

  const total = useMemo(
    () => available.filter((j) => selected.has(j.id)).reduce((sum, j) => sum + Number(j.total), 0),
    [available, selected],
  );

  const toggle = (jobId: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });

  const create = () =>
    run(async () => {
      await api.post('/invoices', { customerId, jobIds: [...selected] });
      setSelected(new Set());
      setCustomerId('');
      onCreated();
    });

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="New invoice"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={create}
            disabled={busy || !customerId || selected.size === 0}
          >
            {busy ? 'Creating…' : `Create · £${total.toFixed(2)}`}
          </button>
        </>
      }
    >
      <div className="stack-4">
        {error ? <div className="notice notice-danger">{error}</div> : null}

        <Field label="Customer">
          <select
            className="select"
            value={customerId}
            onChange={(e) => {
              setCustomerId(e.target.value);
              setSelected(new Set());
            }}
          >
            <option value="">— Choose a customer —</option>
            {(customers?.customers ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>

        {customerId ? (
          available.length === 0 ? (
            <p className="small muted">No completed jobs waiting to be invoiced for this customer.</p>
          ) : (
            <div className="stack-2">
              <span className="label">Completed jobs</span>
              {available.map((job) => (
                <label key={job.id} className={`picker${selected.has(job.id) ? ' is-on' : ''}`}>
                  <input type="checkbox" checked={selected.has(job.id)} onChange={() => toggle(job.id)} />
                  <span className="grow">
                    <span className="listrow-title">{job.title}</span>
                    <span className="listrow-sub">
                      <span className="mono tiny">{job.ref}</span>
                      <span className="dot" />
                      {job.reg_no ?? job.fleet_no}
                    </span>
                  </span>
                  <span className="money">£{Number(job.total).toFixed(2)}</span>
                </label>
              ))}
            </div>
          )
        ) : null}
      </div>
    </Sheet>
  );
}
